import type {
  AbandonedCartRow,
  CartLine,
} from '../../../gateways/breadstack/customer/behaviorCartCheckoutApis'
import type {
  CartRecoveryFieldKey,
  CartRecoveryRequest,
  RecoveryCampaignRequest,
  RecoveryChannel,
  SavedCartRecovery,
} from '../../../gateways/breadstack/customer/cartRecoveryApis'
import type { LoyaltyCouponDraft } from '../../../types/loyaltyCoupon'
import { productName } from './behaviorProducts'
import { formatMoney2 } from './behaviorSearch'
import {
  type RecoveryEmailSettings,
  buildRecoveryBlocks,
  compileRecoveryEmail,
  escapeHtml,
  recoveryEmailSettings,
  textToBlockHtml,
  wrapRecoveryPreview,
} from './recoveryCampaignBlocks'

// Pure logic for the Behavior › Cart & Checkout "Cart Recovery" drawer
// (`docs/pantry/2026.09.09/breadstack-customer-profile (2).html`,
// `openRecovery` … `sendRecovery`). The drawer creates the offer's coupon
// itself (`buildRecoveryCouponDraft`) and then the campaign carrying its code
// (`buildRecoveryCampaignInput`) — see `submitRecovery`.

export type RecoveryOfferType = 'pct' | 'dlr' | 'ship' | 'none'
export type RecoveryExpiryKey = '24h' | '48h' | '72h' | '1w'

export const RECOVERY_EXPIRY_OPTIONS: Array<{
  key: RecoveryExpiryKey
  label: string
  hours: number
}> = [
  { key: '24h', label: '24 hours', hours: 24 },
  { key: '48h', label: '48 hours', hours: 48 },
  { key: '72h', label: '72 hours', hours: 72 },
  { key: '1w', label: '1 week', hours: 168 },
]

export const RECOVERY_CHANNEL_LABEL: Record<RecoveryChannel, string> = {
  email: 'Email',
  sms: 'SMS',
  both: 'Email + SMS',
}

export const RECOVERY_TOKENS = [
  '{first_name}',
  '{cart_total}',
  '{promo_code}',
  '{discount}',
  '{expiry}',
  '{store_name}',
] as const

export const DEFAULT_OFFER_AMOUNT: Record<RecoveryOfferType, number> = {
  pct: 10,
  dlr: 5,
  ship: 0,
  none: 0,
}

// Only % / $ offers carry an amount.
export const offerHasAmount = (offer: RecoveryOfferType) =>
  offer === 'pct' || offer === 'dlr'

export const PROMO_CODE_MAX = 50

export const expiryLabel = (key: RecoveryExpiryKey): string =>
  RECOVERY_EXPIRY_OPTIONS.find((o) => o.key === key)?.label ?? key

// Recovered carts get a disabled "Resolved" button instead of "Recover".
export const canRecover = (cart: Pick<AbandonedCartRow, 'recovery'>) =>
  cart.recovery.status !== 'recovered'

export const lineName = (line: CartLine) => productName(line)

const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

// Design prefixes (`COMEBACK10`, `SAVE5`, `FREESHIP`, `RETURN`) plus a random
// 4-char suffix so codes don't collide across customers, e.g. `COMEBACK10K7Q2`.
export const generatePromoCode = (
  offer: RecoveryOfferType,
  amount: number,
  random: () => number = Math.random,
): string => {
  const prefix =
    offer === 'pct'
      ? `COMEBACK${amount || ''}`
      : offer === 'dlr'
        ? `SAVE${amount || ''}`
        : offer === 'ship'
          ? 'FREESHIP'
          : 'RETURN'
  let suffix = ''
  for (let i = 0; i < 4; i++)
    suffix += CODE_CHARS[Math.floor(random() * CODE_CHARS.length)]
  return `${prefix}${suffix}`
}

export const normalizePromoCode = (raw: string): string =>
  raw.toUpperCase().replace(/[^A-Z0-9]/g, '')

export const offerSummary = (
  offer: RecoveryOfferType,
  amount: number,
): string => {
  if (offer === 'pct') return `${amount}% off`
  if (offer === 'dlr') return `$${amount} off`
  if (offer === 'ship') return 'Free shipping'
  return 'None'
}

// The `{discount}` token + the offer line in the default message.
export const buildOfferLine = (
  offer: RecoveryOfferType,
  amount: number,
): string => {
  if (offer === 'pct')
    return `🎁 As a thank-you, here's ${amount}% off your cart: {promo_code}`
  if (offer === 'dlr')
    return `🎁 We're taking $${amount} off for you: {promo_code}`
  if (offer === 'ship')
    return `🚚 We'll cover your shipping — use code {promo_code}`
  return ''
}

export const buildDefaultSubject = (firstName: string): string =>
  `Hey ${firstName || 'there'}, you left something behind 👀`

// Storefront cart page from the store's `storeUrl` (`https://shop.com/` →
// `https://shop.com/cart`). Empty when the store has no URL.
export const buildCartUrl = (storeUrl: string | undefined | null): string => {
  const base = (storeUrl ?? '').trim().replace(/\/+$/, '')
  if (!base) return ''
  const withScheme = /^https?:\/\//i.test(base) ? base : `https://${base}`
  return `${withScheme}/cart`
}

export const buildDefaultMessage = ({
  channel,
  firstName,
  storeName,
  cartUrl,
  offer,
  amount,
}: {
  channel: RecoveryChannel
  firstName: string
  storeName: string
  // Storefront cart link (see `buildCartUrl`); the line is omitted when
  // empty. The campaign email links the cart with its CTA button instead.
  cartUrl?: string
  offer: RecoveryOfferType
  amount: number
}): string => {
  const name = firstName || 'there'
  const offerLine = buildOfferLine(offer, amount)
  const offerBlock = offerLine ? `\n\n${offerLine}` : ''
  if (channel === 'sms') {
    const linkBlock = cartUrl ? `\n\nShop now → ${cartUrl}` : ''
    return `Hi ${name}! 👋 You left {cart_total} in your cart at ${storeName}.${offerBlock}${linkBlock}\n\nReply STOP to unsubscribe.`
  }
  const linkBlock = cartUrl ? `\n\n👉 Complete your order → ${cartUrl}` : ''
  const codeBlock =
    offer !== 'none'
      ? '\n\nUse code {promo_code} at checkout. Expires in {expiry}.'
      : ''
  return `Hi ${name},

You left something in your cart and we don't want you to miss out! 🛒

Your cart ({cart_total}) is still waiting for you.${offerBlock}${linkBlock}${codeBlock}

Questions? Reply to this email anytime.

— The ${storeName} Team`
}

// ── Templates ────────────────────────────────────────────────────────────────
// The Campaign Builder's Abandoned Cart starters (`CART_TEMPLATES`,
// Loyalty/marketing/builder/blocks/starters.ts on the block builder): a
// template seeds the subject, preview text, message and offer, and is sent as
// the campaign's `template_key` with its starter blocks
// (`buildRecoveryEmail`). Each sends one email — the drawer has no sequences.

export type RecoveryTemplateKey =
  | 'blank'
  | 'cart_single'
  | 'cart_sequence'
  | 'cart_offer'

export interface RecoveryTemplate {
  key: Exclude<RecoveryTemplateKey, 'blank'>
  name: string
  desc: string
  badge?: 'popular'
}

export const RECOVERY_TEMPLATES: RecoveryTemplate[] = [
  {
    key: 'cart_single',
    name: 'Single Reminder',
    desc: 'A friendly reminder with the cart and your offer',
    badge: 'popular',
  },
  {
    key: 'cart_sequence',
    name: '3-Step Sequence',
    desc: 'Step 1 of a nudge sequence: a reminder without an offer',
  },
  {
    key: 'cart_offer',
    name: 'Offer First',
    desc: 'Lead with a discount code in the first message',
  },
]

// "10% off" / "$5 off" / "free shipping"; '' for no offer.
const offerShort = (offer: RecoveryOfferType, amount: number): string =>
  offer === 'none' ? '' : offerSummary(offer, amount).replace(/^Free/, 'free')

// The offer a template starts with: the sequence's first step is a plain
// nudge; the others lead with a code (keeping the current one if set).
export const templateOffer = (
  key: RecoveryTemplateKey,
  current: RecoveryOfferType,
): RecoveryOfferType => {
  if (key === 'cart_sequence') return 'none'
  if (key === 'blank') return current
  return current === 'none' ? 'pct' : current
}

export const buildTemplateSubject = (
  key: RecoveryTemplateKey,
  {
    firstName,
    offer,
    amount,
  }: {
    firstName: string
    offer: RecoveryOfferType
    amount: number
  },
): string => {
  if (key === 'cart_single') {
    const short = offerShort(offer, amount)
    return short
      ? `You left something behind — here's ${short} 🛒`
      : 'You left something behind 🛒'
  }
  if (key === 'cart_sequence') return 'Complete your order — Step 1 of 3'
  if (key === 'cart_offer') return "Your cart is waiting — and here's a gift 🎁"
  return buildDefaultSubject(firstName)
}

// The email's headline: the starters share the Campaign Builder's cart copy;
// blank leads with the subject.
export const buildTemplateHeadline = (
  key: RecoveryTemplateKey,
  subject: string,
): string => (key === 'blank' ? subject : 'You left something behind')

export const buildTemplatePreviewText = (key: RecoveryTemplateKey): string => {
  if (key === 'cart_single' || key === 'cart_sequence')
    return 'Your cart is saved and ready when you are.'
  if (key === 'cart_offer')
    return 'We saved your cart — and added a little gift.'
  return ''
}

export const buildTemplateMessage = (
  key: RecoveryTemplateKey,
  args: Parameters<typeof buildDefaultMessage>[0],
): string => {
  if (key === 'blank' || args.channel === 'sms')
    return buildDefaultMessage(args)
  const short = offerShort(args.offer, args.amount)
  const codeLine = short
    ? `\n\nHere's ${short} to help you finish — use code {promo_code} at checkout. It expires in {expiry}.`
    : ''
  if (key === 'cart_offer')
    return short
      ? `Hi {first_name}, we saved your cart ({cart_total}) — and added a little gift: ${short} with code {promo_code}.\n\nIt expires in {expiry}, so don't wait too long.`
      : 'Hi {first_name}, we saved your cart ({cart_total}). Complete your order before your picks sell out.'
  if (key === 'cart_sequence')
    return `Hi {first_name}, your cart ({cart_total}) is saved and ready when you are. Complete your order before your picks sell out.${codeLine}`
  return `Hi {first_name}, your cart is saved and ready when you are. Complete your order before your picks sell out.${codeLine}`
}

export interface RecoveryTokenContext {
  firstName: string
  cartTotal: number
  promoCode: string
  offer: RecoveryOfferType
  amount: number
  expiry: RecoveryExpiryKey
  storeName: string
}

// Resolves every `{token}` for the preview. `{discount}` expands to the offer
// line, which itself contains `{promo_code}` — so it is substituted first.
export const resolveTokens = (text: string, ctx: RecoveryTokenContext) =>
  text
    .replace(/\{discount\}/g, buildOfferLine(ctx.offer, ctx.amount))
    .replace(/\{first_name\}/g, ctx.firstName || 'there')
    .replace(/\{cart_total\}/g, formatMoney2(ctx.cartTotal))
    .replace(/\{promo_code\}/g, ctx.offer === 'none' ? '' : ctx.promoCode)
    .replace(/\{expiry\}/g, expiryLabel(ctx.expiry))
    .replace(/\{store_name\}/g, ctx.storeName)

const pad = (n: number) => String(n).padStart(2, '0')

export interface RecoveryForm {
  template: RecoveryTemplateKey
  channel: RecoveryChannel
  offer: RecoveryOfferType
  amount: number
  promoCode: string
  expiry: RecoveryExpiryKey
  subject: string
  // Inbox preheader shown under the subject; '' = none.
  previewText: string
  body: string
  scheduled: boolean
  // `YYYY-MM-DD` / `HH:mm` from the native inputs.
  scheduleDate: string
  scheduleTime: string
  // Email brand colour override (#rrggbb); '' = the store's brand colour.
  brandColor: string
}

// Keys match the server's field errors (`parseCartRecoveryError`) so both
// render next to the same control.
export type RecoveryFormErrors = Partial<Record<CartRecoveryFieldKey, string>>

export const scheduledAtIso = (date: string, time: string): string | null => {
  if (!date || !time) return null
  const d = new Date(`${date}T${time}`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

// A draft keeps the schedule choice but only sends it while it's in the
// future (as the Campaign Builder does), so a past time isn't an error there.
export const validateRecoveryForm = (
  form: RecoveryForm,
  now: number = Date.now(),
  { draft = false }: { draft?: boolean } = {},
): RecoveryFormErrors => {
  const errors: RecoveryFormErrors = {}
  if (offerHasAmount(form.offer)) {
    const a = form.amount
    if (!Number.isFinite(a) || a <= 0) errors.amount = 'Enter an amount'
    else if (form.offer === 'pct' && a > 100) errors.amount = 'Max 100%'
  }
  if (form.offer !== 'none') {
    const code = normalizePromoCode(form.promoCode)
    if (!code) errors.promoCode = 'Enter a promo code'
    else if (code.length > PROMO_CODE_MAX)
      errors.promoCode = `Max ${PROMO_CODE_MAX} characters`
  }
  if (form.channel !== 'sms' && !form.subject.trim())
    errors.subject = 'Enter a subject line'
  if (!form.body.trim()) errors.body = 'Enter a message'
  if (form.scheduled && !draft) {
    const iso = scheduledAtIso(form.scheduleDate, form.scheduleTime)
    if (!iso) errors.schedule = 'Pick a date and time'
    else if (new Date(iso).getTime() <= now)
      errors.schedule = 'Pick a time in the future'
  }
  return errors
}

// Default schedule = tomorrow 18:30 (inside the design's 6–9 PM hint).
export const defaultSchedule = (now: Date = new Date()) => {
  const d = new Date(now)
  d.setDate(d.getDate() + 1)
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: '18:30',
  }
}

// Drawer state → POST body. Message text keeps its raw `{tokens}`.
export const buildRecoveryRequest = (
  cart: AbandonedCartRow,
  form: RecoveryForm,
): CartRecoveryRequest => ({
  sessionId: cart.id,
  cart: {
    items: cart.lines.map((l) => ({
      productId: l.productId,
      name: lineName(l),
      imageUrl: l.imageUrl,
      price: Math.max(0, l.price),
      quantity: Math.max(1, Math.round(l.quantity)),
    })),
    total: cart.value,
    stage: cart.stage,
    abandonedAt: cart.abandonedAt || undefined,
  },
  channel: form.channel,
  offer: {
    type: form.offer,
    amount: offerHasAmount(form.offer) ? form.amount : undefined,
    promoCode:
      form.offer !== 'none' ? normalizePromoCode(form.promoCode) : undefined,
    expiry: form.offer !== 'none' ? form.expiry : undefined,
  },
  message: {
    subject: form.channel !== 'sms' ? form.subject : undefined,
    body: form.body,
  },
  scheduledAt: form.scheduled
    ? (scheduledAtIso(form.scheduleDate, form.scheduleTime) ?? undefined)
    : undefined,
})

export interface RecoveryToast {
  message: string
  variant: 'success' | 'error'
}

const channelError = (r: SavedCartRecovery): string | null => {
  for (const ch of ['email', 'sms'] as const) {
    const res = r.results[ch]
    if (res && (res.status === 'failed' || res.status === 'skipped'))
      return `${RECOVERY_CHANNEL_LABEL[ch]} ${res.status}${res.error ? `: ${res.error}` : ''}`
  }
  return null
}

// Final-state toast for a Send Now recovery (`null` = still pending after the
// poll window). Surfaces whether the backend-created coupon made it.
export const describeRecoveryOutcome = (
  r: SavedCartRecovery | null,
  ctx: { to: string; offerLabel: string },
): RecoveryToast => {
  if (!r)
    return {
      message: 'Recovery is still sending — check back in a minute.',
      variant: 'success',
    }
  const channel = RECOVERY_CHANNEL_LABEL[r.channel]
  const couponPart =
    r.couponId && r.promoCode
      ? ` · ${ctx.offerLabel} · Code: ${r.promoCode}`
      : ''
  if (r.status === 'sent')
    return {
      message: `✓ Recovery ${channel} sent to ${ctx.to}${couponPart}`,
      variant: 'success',
    }
  if (r.status === 'partial')
    return {
      message: `Recovery partially sent${couponPart} — ${channelError(r) ?? 'one channel failed'}`,
      variant: 'error',
    }
  if (r.status === 'canceled')
    return { message: 'Recovery was canceled.', variant: 'error' }
  const couponError = r.results.coupon?.error
  return {
    message: couponError
      ? `Recovery failed — coupon couldn’t be created: ${couponError}`
      : `Recovery failed — ${channelError(r) ?? 'please try again'}`,
    variant: 'error',
  }
}

// "Oct 16, 6:30 PM" in the user's timezone.
export const formatScheduledAt = (iso: string | null): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// ── Campaign mode ───────────────────────────────────────────────────────────
// The drawer creates a one-customer Abandoned Cart campaign
// (`POST /loyalty/campaigns`) in the Campaign Builder's block format. The
// campaign renderer uses `{{token}}`s, so the drawer's `{token}`s are mapped;
// `{discount}` / `{expiry}` are fixed by the offer and resolved here, and so
// is `{cart_total}` when the cart is known: campaign sends carry no cart
// context, so the backend renders `{{cart_total}}` empty. The cart link is
// the email's CTA button (`{{cart_url}}`).

const CAMPAIGN_TOKEN: Record<string, string> = {
  first_name: '{{first_name}}',
  cart_total: '{{cart_total}}',
  store_name: '{{store_name}}',
  promo_code: '{{coupon}}',
}

export const toCampaignText = (
  text: string,
  offer: RecoveryOfferType,
  amount: number,
  expiry: RecoveryExpiryKey,
  // The abandoned cart's value; when given, `{cart_total}` is filled in.
  cartTotal?: number,
): string =>
  text
    .replace(/\{discount\}/g, buildOfferLine(offer, amount))
    .replace(/\{expiry\}/g, expiryLabel(expiry))
    .replace(/(?<!\{)\{cart_total\}(?!\})/g, (token) =>
      cartTotal === undefined ? token : formatMoney2(cartTotal),
    )
    .replace(
      /(?<!\{)\{(first_name|cart_total|store_name|promo_code)\}(?!\})/g,
      (_, key: string) =>
        key === 'promo_code' && offer === 'none' ? '' : CAMPAIGN_TOKEN[key],
    )

// Matches the Campaign Builder's cart starters (`CART_TEMPLATES` ctaText).
export const RECOVERY_CTA_TEXT = 'Complete Your Order'

export interface RecoveryCustomerState {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
}

// "Cart recovery – Ana Li – Oct 20"
export const recoveryCampaignName = (
  customer: RecoveryCustomerState,
  customerId: number | string,
  now: Date = new Date(),
): string => {
  const label =
    [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim() ||
    customer.email ||
    `#${customerId}`
  const date = now.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
  return `Cart recovery – ${label} – ${date}`
}

// The created recovery campaign in the Campaign Builder (edit view), linked
// from the success toast and opened by "Edit in builder".
export const buildRecoveryCampaignUrl = (
  storeId: string,
  campaignId: string,
): string =>
  `/s/${storeId}/loyalty/campaigns/builder/${encodeURIComponent(campaignId)}`

export const recoveryPromoCode = (form: RecoveryForm): string =>
  form.offer !== 'none' ? normalizePromoCode(form.promoCode) : ''

// The store's brand colour unless the drawer overrides it.
export const recoveryBrandColor = (form: RecoveryForm, storeBrand: string) =>
  form.brandColor || storeBrand

export interface RecoveryEmailContext {
  storeName: string
  storeUrl: string
  // Store brand (`/loyalty/campaigns/brand-logo`); '' = none.
  brandColor: string
  brandLogoUrl: string
}

export interface RecoveryEmail {
  blocks: ReturnType<typeof buildRecoveryBlocks>
  settings: RecoveryEmailSettings
  // `compiled_html`: body only, `{{tokens}}` + cart markers for the backend.
  compiledHtml: string
}

const blocksInput = (
  form: RecoveryForm,
  ctx: RecoveryEmailContext,
  text: (raw: string) => string,
) => {
  const code = recoveryPromoCode(form)
  return {
    withHero: form.template !== 'blank',
    storeName: ctx.storeName,
    headlineHtml: textToBlockHtml(
      text(buildTemplateHeadline(form.template, form.subject)),
    ),
    bodyHtml: textToBlockHtml(text(form.body)),
    couponCode: code,
    couponTitle: code ? offerSummary(form.offer, form.amount) : '',
    couponNote: code ? `Expires in ${expiryLabel(form.expiry)}` : '',
    ctaText: RECOVERY_CTA_TEXT,
  }
}

// The campaign's email: the template's starter blocks with the drawer's copy,
// the brand colour as `email_settings`, compiled for the backend.
export const buildRecoveryEmail = (
  form: RecoveryForm,
  ctx: RecoveryEmailContext & { cartTotal: number },
): RecoveryEmail => {
  const settings = recoveryEmailSettings(
    recoveryBrandColor(form, ctx.brandColor),
  )
  const blocks = buildRecoveryBlocks(
    blocksInput(form, ctx, (raw) =>
      toCampaignText(
        escapeHtml(raw),
        form.offer,
        form.amount,
        form.expiry,
        ctx.cartTotal,
      ),
    ),
  )
  return {
    blocks,
    settings,
    compiledHtml: compileRecoveryEmail(blocks, settings, {
      target: 'email',
      brandLogoUrl: ctx.brandLogoUrl,
      storeName: ctx.storeName,
      storeUrl: ctx.storeUrl,
    }),
  }
}

// The same email for the drawer's preview iframe: tokens resolved for this
// customer / cart, links inert.
export const buildRecoveryPreviewHtml = (
  form: RecoveryForm,
  ctx: RecoveryEmailContext & {
    tokens: RecoveryTokenContext
    lines: CartLine[]
  },
): string => {
  const settings = recoveryEmailSettings(
    recoveryBrandColor(form, ctx.brandColor),
  )
  const resolve = (raw: string) => escapeHtml(resolveTokens(raw, ctx.tokens))
  const blocks = buildRecoveryBlocks(blocksInput(form, ctx, resolve))
  return wrapRecoveryPreview(
    compileRecoveryEmail(blocks, settings, {
      target: 'preview',
      brandLogoUrl: ctx.brandLogoUrl,
      storeName: ctx.storeName,
      storeUrl: ctx.storeUrl,
      lines: ctx.lines,
    }),
    settings,
    resolveTokens(form.previewText, ctx.tokens),
  )
}

export type RecoverySendMode = 'now' | 'schedule' | 'draft'

// What the primary button / Save draft send: a scheduled form sends
// 'schedule', a draft keeps its schedule choice in `schedule_mode`.
export const recoverySendMode = (
  form: RecoveryForm,
  draft: boolean,
): RecoverySendMode => (draft ? 'draft' : form.scheduled ? 'schedule' : 'now')

export const buildRecoveryCampaignInput = (
  cart: AbandonedCartRow,
  form: RecoveryForm,
  ctx: RecoveryEmailContext & {
    customerId: number
    customer: RecoveryCustomerState
    send: RecoverySendMode
    now?: Date
  },
): RecoveryCampaignRequest => {
  const { offer, amount, expiry } = form
  const now = ctx.now ?? new Date()
  const at = form.scheduled
    ? scheduledAtIso(form.scheduleDate, form.scheduleTime)
    : null
  // A draft only keeps a schedule that's still in the future.
  const scheduledAt =
    at && (ctx.send === 'schedule' || new Date(at).getTime() > now.getTime())
      ? at
      : null
  const email = buildRecoveryEmail(form, { ...ctx, cartTotal: cart.value })
  return {
    name: recoveryCampaignName(ctx.customer, ctx.customerId, now),
    type: 'abandoned_cart',
    channel: 'email',
    audience: { type: 'ids', customer_ids: [String(ctx.customerId)] },
    recovery_cart: {
      session_id: cart.id,
      total: cart.value,
      customer_id: ctx.customerId,
    },
    send: ctx.send,
    schedule_mode: form.scheduled ? 'schedule' : 'now',
    ...(scheduledAt ? { scheduled_at: scheduledAt } : {}),
    subject: toCampaignText(
      form.subject,
      offer,
      amount,
      expiry,
      cart.value,
    ).trim(),
    preview_text: toCampaignText(
      form.previewText,
      offer,
      amount,
      expiry,
      cart.value,
    ).trim(),
    coupon_code: recoveryPromoCode(form),
    content_mode: 'blocks',
    blocks: email.blocks,
    email_settings: email.settings,
    compiled_html: email.compiledHtml,
    template_key: form.template,
  }
}

// ── Coupon ──────────────────────────────────────────────────────────────────
// The offer's coupon, created by the drawer before the campaign: single-use,
// locked to this customer's email, storewide, expiring `expiry` after the
// email goes out.

// When the email goes out: the schedule for a scheduled send, else now
// (drafts too — the expiry can be edited in Coupons if it's sent later).
export const recoverySendAt = (
  form: RecoveryForm,
  send: RecoverySendMode,
  now: Date = new Date(),
): Date => {
  if (send !== 'schedule') return now
  const at = scheduledAtIso(form.scheduleDate, form.scheduleTime)
  return at ? new Date(at) : now
}

// `YYYY-MM-DDTHH:mm` in the browser's timezone — the coupon draft's expiry
// format (the coupon mappers derive the UTC field from it).
const localDateTime = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`

export const buildRecoveryCouponDraft = (
  form: RecoveryForm,
  ctx: {
    email: string
    customer: RecoveryCustomerState
    customerId: number | string
    sendAt: Date
  },
): LoyaltyCouponDraft => {
  const hours =
    RECOVERY_EXPIRY_OPTIONS.find((o) => o.key === form.expiry)?.hours ?? 48
  const expiresAt = new Date(ctx.sendAt.getTime() + hours * 3600 * 1000)
  const name = recoveryCampaignName(ctx.customer, ctx.customerId, ctx.sendAt)
  return {
    code: normalizePromoCode(form.promoCode),
    name,
    description: `${offerSummary(form.offer, form.amount)} for ${ctx.email}`,
    icon: '',
    appliesTo: 'all',
    webCategories: [],
    products: [],
    discountType: form.offer === 'pct' ? 'percent' : 'fixed',
    fixedTarget: 'cart',
    value: form.amount,
    limits: {
      expiresAt: localDateTime(expiresAt),
      perPerson: 1,
      total: 1,
      minOrder: null,
      maxDiscount: null,
      excludeSale: false,
      stackable: false,
    },
    emailRestrictions: [ctx.email],
  }
}

// ── Submit ──────────────────────────────────────────────────────────────────

export class RecoverySubmitError extends Error {
  constructor(
    // Which request failed: the coupon (nothing was created) or the campaign
    // (the coupon was rolled back).
    readonly stage: 'coupon' | 'campaign',
    // The request's error, as the httpClient threw it.
    readonly error: unknown,
  ) {
    super(`Cart recovery ${stage} failed`)
  }
}

// Coupon first, then the campaign carrying its code; a failed campaign
// deletes the coupon again (best effort) so no orphan code is left behind.
export const submitRecovery = async <C extends { id: string }>(p: {
  couponDraft: LoyaltyCouponDraft | null
  createCoupon: (draft: LoyaltyCouponDraft) => Promise<{ id: string }>
  deleteCoupon: (id: string) => Promise<unknown>
  createCampaign: () => Promise<C>
}): Promise<C> => {
  let couponId: string | null = null
  if (p.couponDraft) {
    try {
      couponId = (await p.createCoupon(p.couponDraft)).id
    } catch (err) {
      throw new RecoverySubmitError('coupon', err)
    }
  }
  try {
    return await p.createCampaign()
  } catch (err) {
    if (couponId) await p.deleteCoupon(couponId).catch(() => undefined)
    throw new RecoverySubmitError('campaign', err)
  }
}
